import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Renderer2,
  Self,
} from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors } from '@angular/forms';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasFormBaseComponent } from '../gas-form-base-component';
import { GasIconColor } from '../gas-input/gas-input.interface';
import { GasDatepickerTime } from '../gas-datepicker/gas-datepicker.interface';
import { DATE_FORMATS, DateFormat } from '../../utils/gas-date-directive/date-format.type';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';

@Component({
  selector: 'gas-datepicker-input',
  templateUrl: './gas-datepicker-input.component.html',
  styleUrls: ['./gas-datepicker-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasDatepickerInputComponent extends GasFormBaseComponent implements OnInit, OnChanges {

  /** флаг для отображения поля ввода времени */
  @GasInputBoolean()
  @Input()
  public timesPicker = false;

  @Input()
  public timeWithoutSeconds = false;

  @Input()
  public defaultTime: GasDatepickerTime;

  /** минимально доступный для выбора день */
  @Input()
  public minDate: Date;

  /** максимально доступный для выбора день */
  @Input()
  public maxDate: Date;

  @Input()
  public format: DateFormat = 'dd.MM.yyyy';

  @Input()
  public label: string;

  @Input()
  public postfixIconColor: GasIconColor | string = 'default';

  /** Текст ошибки */
  @Input()
  public error: string;

  /** Текст плейсхолдера */
  @Input()
  public placeholder = '';

  /** Текущий выбранный день */
  @Input()
  public set presetDate(presetDate: Date | string) {
    if (presetDate) {
      this.selectedDate = typeof presetDate === 'string'
        ? parse(this.normalizeValueToFormat(presetDate), this.format, new Date())
        : presetDate;

      this.form.setValue(presetDate, { emitEvent: false });
    }
  }

  /** Возвращает выбранную дату */
  public get presetDate(): Date | string {
    return this.selectedDate;
  }

  /** Выбранная дата */
  private selectedDate: Date;

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public get inputMask(): string {
    return this.format.replace(/[a-zA-Z]/ig, '_');
  }

  /**
   * Инициализация при загрузке страницы
   */
  public ngOnInit(): void {
    this.form.setValue(this.getInputValue(this.ngControl?.value), { emitEvent: false });
    this.form.setValidators(this.customDatePickerValidator.bind(this));
  }

  public inputClickHandler(event: Event): void {
    // не знаю зачем тут изначально было stopPropagation, но оно блочит выполнение GasClickOutsideDirective
    // и появляется возможность открыть несколько датапикеров
    //event.stopPropagation();

    // Не уверен что обработаны все случаи disabled, надо потестить
    if (this.nativeDisabled) {
      return;
    }
  }

  public validate(): boolean {
    if (!this.form?.dirty) {
      this.error = undefined;
      return true;
    }

    if (!this.form.value) {
      if (this.required) {
        this.error = 'Дата не указана';
        return false;
      }
      this.error = undefined;
      return true;
    }

    if (this.form.value.length !== this.format.length) {
      this.error = 'Дата не соответствует установленному формату';
      return false;
    }

    const date = parse(this.form.value, this.format, new Date());
    if (!isValid(date)) {
      this.error = 'Дата указана не верно';
      return false;
    }

    if (this.minDate && isBefore(date, parse(format(this.minDate, this.format), this.format, new Date()))) {
      this.error = `Дата не может быть меньше ${format(this.minDate, this.format)}`;
      return false;
    }

    if (this.maxDate && isAfter(date, parse(format(this.maxDate, this.format), this.format, new Date()))) {
      this.error = `Дата не может быть больше ${format(this.maxDate, this.format)}`;
      return false;
    }

    this.error = undefined;
    return true;
  }

  private customDatePickerValidator(control: AbstractControl): ValidationErrors | null {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return !this.validate() ? { INPUT_DATE_ERROR: true } : null;
  };

  private getInputValue(date: Date | string): string {
    if (!Boolean(date)) {
      return '';
    }

    return typeof date === 'string'
      ? this.normalizeValueToFormat(date)
      : format(date, this.format);
  }

  private normalizeValueToFormat(date: string): string {
    if (!date) {
      return date;
    }

    const dateFormat: string = this.getCurrentFormat(date);

    return dateFormat
      ? format(parse(date, dateFormat, new Date()), this.format)
      : format(new Date(), this.format);
  }

  private getCurrentFormat(date: string): string {
    return [
      this.format,
      ...DATE_FORMATS,
    ].find(currFormat => {
      return isValid(parse(date, currFormat, new Date()));
    });
  }
}
